import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Input,
  Space,
  Typography,
  Row,
  Col,
  Skeleton,
  ConfigProvider,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { showVisumEffect } from "../../components/utils/antWaveEffect";
import { toast } from "../../components/toasts/toastStore";
import { apiAseguradoras, authHeader } from "../../redux/actions/catalogos/catalogosApi";
import { BannerFoto } from "./EntidadFotoCard";
import "./PaquetesPage.css";
import { usePermisos } from "../../context/PermisosContext";

const { Title, Text, Paragraph } = Typography;

const BANNER_BG = "linear-gradient(135deg, var(--brand-dark, #3d5590) 0%, var(--brand-primary) 100%)";

export default function AseguradorasPage() {
  const navigate = useNavigate();
  const { perm } = usePermisos() || { perm: () => true };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [activeFilter, setActiveFilter] = useState("todos");

  const lastFetchKey = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchAseguradoras = () => {
    setLoading(true);
    apiAseguradoras
      .get("/aseguradoras", { headers: authHeader(), params: { search: searchDebounced || undefined } })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data?.items ?? [];
        setItems(data);
      })
      .catch(() => toast.error("No se pudo cargar el catalogo de aseguradoras"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const key = searchDebounced;
    if (lastFetchKey.current === key) return;
    lastFetchKey.current = key;
    fetchAseguradoras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDebounced]);

  const counts = useMemo(
    () => ({
      total: items.length,
      conConvenio: items.filter((i) => Boolean(i.convenio_directo)).length,
      sinConvenio: items.filter((i) => !i.convenio_directo).length,
    }),
    [items]
  );

  const filteredItems = useMemo(() => {
    if (activeFilter === "con_convenio") return items.filter((i) => Boolean(i.convenio_directo));
    if (activeFilter === "sin_convenio") return items.filter((i) => !i.convenio_directo);
    return items;
  }, [items, activeFilter]);

  const STAT_CARDS = [
    { key: "todos", label: "Total de aseguradoras", icon: <i className="fa-solid fa-building-shield" />, iconCls: "paq-stat-icon-sonido", count: counts.total },
    { key: "con_convenio", label: "Con convenio directo", icon: <i className="fa-solid fa-handshake" />, iconCls: "paq-stat-icon-decoracion", count: counts.conConvenio },
    { key: "sin_convenio", label: "Sin convenio directo", icon: <i className="fa-solid fa-ban" />, iconCls: "paq-stat-icon-barra", count: counts.sinConvenio },
  ];

  return (
    <ConfigProvider wave={{ showEffect: showVisumEffect }}>
      <main className="paq-main">
        <div className="paq-content">
          <section className="paq-section">
            <div className="paq-filters-panel">
              <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
                <Title level={2} className="paq-title">Aseguradoras</Title>
                <Text className="paq-subtitle">Catalogo de aseguradoras con las que trabajan los pacientes del consultorio.</Text>
              </Space>

              <Row gutter={[16, 14]}>
                <Col xs={24}>
                  <div className="paq-field-label">Buscador</div>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por nombre..."
                    suffix={<SearchOutlined className="paq-input-suffix" />}
                    className="paq-control"
                    allowClear
                  />
                </Col>
              </Row>

              <Row gutter={[16, 14]} style={{ marginTop: 14 }} align="bottom">
                <Col xs={24} lg={6}>
                  <div className="paq-actions">
                    <Button
                      className="paq-btn-clean"
                      onClick={() => {
                        setSearch("");
                        setActiveFilter("todos");
                      }}
                    >
                      Limpiar filtros
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="paq-stats-row" style={{ gridTemplateColumns: `repeat(${STAT_CARDS.length}, minmax(0, 1fr))` }}>
              {STAT_CARDS.map(({ key, label, icon, iconCls, count }) => (
                <Card
                  key={key}
                  className={`paq-stat-card ${activeFilter === key ? "paq-stat-active" : ""}`}
                  hoverable
                  onClick={() => setActiveFilter((p) => (p === key ? "todos" : key))}
                >
                  <Space align="center" size={10}>
                    <div className={`paq-stat-icon ${iconCls}`}>{icon}</div>
                    <div>
                      <div className="paq-stat-value">{count}</div>
                      <div className="paq-stat-label">{label}</div>
                    </div>
                  </Space>
                </Card>
              ))}
            </div>

            <div className="paq-expedientes-card">
              <div className="paq-toolbar">
                <div className="paq-toolbar-left">
                  <Title level={4} style={{ marginBottom: 0 }}>Aseguradoras ({filteredItems.length})</Title>
                  <Text type="secondary">{filteredItems.length} resultados encontrados</Text>
                </div>
                <div className="paq-toolbar-right">
                  {perm("aseguradoras", "insertar") && (
                    <Button
                    type="primary"
                    icon={<i className="fa-solid fa-plus" />}
                    onClick={() => navigate("/app/aseguradoras/crear")}
                    style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
                  >
                    Nueva aseguradora
                  </Button>
                  )}
                </div>
              </div>

              {!loading && filteredItems.length === 0 && (
                <div className="paq-empty">
                  <div style={{ fontSize: 16, fontWeight: 500 }}>Sin aseguradoras que coincidan con los filtros</div>
                </div>
              )}

              <div className="paq-grid">
                {loading &&
                  Array.from({ length: 8 }).map((_, i) => (
                    <div key={`sk-${i}`} className="paq-card paq-card-skeleton">
                      <div className="paq-card-header">
                        <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1, width: "40%" }} />
                      </div>
                      <div className="paq-card-body">
                        <Skeleton active title={false} paragraph={{ rows: 3 }} />
                      </div>
                    </div>
                  ))}

                {!loading &&
                  filteredItems.map((aseguradora) => (
                    <div key={aseguradora.id_aseguradora} className="paq-card">
                      <div className="paq-card-banner" style={{ background: BANNER_BG }}>
                        <BannerFoto item={aseguradora} icon="fa-building-shield" />
                      </div>

                      <div className="paq-card-header">
                        <div className="paq-card-head">
                          <Paragraph className="paq-card-name" ellipsis={{ rows: 2 }}>{aseguradora.nombre}</Paragraph>
                          {Boolean(aseguradora.convenio_directo) && (
                            <span className="paq-tipo-badge paq-tipo-decoracion">Convenio directo</span>
                          )}
                        </div>
                      </div>

                      <div className="paq-card-body">
                        <div className="paq-contenidos-box">
                          <i className="fa-solid fa-user paq-info-icon" />
                          <div className="paq-info-text">
                            <span className="paq-info-label">Pacientes</span>
                            <span className="paq-info-value">{aseguradora.pacientes_count ?? 0}</span>
                          </div>
                        </div>
                      </div>

                      <div className="paq-card-footer">
                        <Button
                          className="paq-btn-details"
                          icon={<i className="fa-solid fa-circle-info" />}
                          onClick={() => navigate(`/app/aseguradoras/${aseguradora.id_aseguradora}`)}
                        >
                          Ver detalles
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </section>
        </div>
      </main>
    </ConfigProvider>
  );
}
